import type { CSSProperties, ReactNode } from 'react';

export type ProgressRingProps = {
  value?: number;
  max?: number;
  size?: number;
  thickness?: number;
  color?: string;
  track?: string;
  label?: ReactNode;
  sublabel?: ReactNode;
  showValue?: boolean;
  style?: CSSProperties;
};

/** ProgressRing — circular gauge for daily goals (items packed, feeds done). */
export function ProgressRing({
  value = 0,
  max = 100,
  size = 96,
  thickness = 10,
  color = 'var(--brand)',
  track = 'var(--sand-200)',
  label,
  sublabel,
  showValue = true,
  style,
}: ProgressRingProps) {
  const pct = Math.max(0, Math.min(1, max === 0 ? 0 : value / max));
  const r = (size - thickness) / 2;
  const c = 2 * Math.PI * r;
  const dash = c * pct;

  const valueStyle: CSSProperties = {
    fontFamily: 'var(--font-mono)',
    fontVariantNumeric: 'tabular-nums',
    fontSize: size / 4.2,
    fontWeight: 'var(--fw-medium)' as unknown as number,
    color: 'var(--text)',
    lineHeight: 1,
  };

  return (
    <div
      role="progressbar"
      aria-valuenow={value}
      aria-valuemin={0}
      aria-valuemax={max}
      style={{ position: 'relative', width: size, height: size, display: 'inline-flex', flexShrink: 0, ...style }}
    >
      <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={track} strokeWidth={thickness} />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke={color}
          strokeWidth={thickness}
          strokeLinecap="round"
          strokeDasharray={`${dash} ${c}`}
          style={{ transition: 'stroke-dasharray var(--duration-slow) var(--ease-soft)' }}
        />
      </svg>
      <div
        style={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 0,
        }}
      >
        {label != null ? (
          <span style={valueStyle}>{label}</span>
        ) : showValue ? (
          <span style={valueStyle}>{Math.round(pct * 100)}%</span>
        ) : null}
        {sublabel ? (
          <span style={{ fontSize: size / 9, color: 'var(--text-subtle)', marginTop: 2 }}>{sublabel}</span>
        ) : null}
      </div>
    </div>
  );
}
